// Libraries
import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimer } from '@fortawesome/pro-solid-svg-icons';

// Hooks
import { useAppSelector, useAppDispatch } from '../../../app/hooks';

// Models
import { LobbySession as Session } from '../../../features/lobby/lobbyModels';

// Features
import { selectUser } from '../../../features/user/userSlice';
import {
    fetchSessionsAsync,
    selectActiveSessions,
    selectLobbyStatus,
    selectLobbyError,
} from '../../../features/lobby/lobbySlice';

// Components
import StateHandler from '../StateScreen/StateHandler';
import ContentRegularSessions from '../../organisms/Content/ContentRegularSessions';
import ContentFeaturedSessions from '../../organisms/Content/ContentFeaturedSessions';
// import ContentPrivateSessions from '../../organisms/Content/ContentPrivateSessions';
import LobbyGrid from '../../organisms/LobbyGrid/LobbyGrid';
import LobbySession from '../../molecules/LobbySession/LobbySession';

// Styles
import styles from './Lobby.module.css';
import StateScreen from '../StateScreen/StateScreen';

/**
 * @function Lobby
 * Describes a list of available game sessions
 *
 * @example
 * return (
 *   <Lobby />
 * )
 */
function Lobby(): JSX.Element {
    const [menuVisible, setMenuVisible] = useState<boolean>(false);
    const [activeContent, setActiveContent] = useState<string>('lobby');
    const dispatch = useAppDispatch();
    const user = useAppSelector(selectUser);
    const activeSessions = useAppSelector(selectActiveSessions);
    const lobbyStatus = useAppSelector(selectLobbyStatus);
    const lobbyError = useAppSelector(selectLobbyError);

    function fetchSessions() {
        if (lobbyStatus !== 'loading') {
            dispatch(fetchSessionsAsync());
        }
    }

    useEffect(() => {
        let interval: ReturnType<typeof setInterval> | null = null;
        const toExactMinute: number = 60000 - (new Date().getTime() % 60000);
        const timeout = setTimeout(() => {
            interval = setInterval(() => {
                fetchSessions();
            }, 60 * 1000);
            fetchSessions();
        }, toExactMinute);

        fetchSessions();

        return () => {
            interval !== null && clearInterval(interval);
            clearTimeout(timeout);
        };
    }, []);

    if (lobbyStatus !== 'idle') {
        return (
            <StateHandler
                status={lobbyStatus}
                error={lobbyError}
                loadingText="Loading sessions..."
            />
        );
    }

    if (activeSessions.length < 1) {
        return (
            <StateScreen
                icon={<FontAwesomeIcon icon={faTimer} />}
                title="Sorry, there are no sessions currently running"
            >
                <p>Please check back soon.</p>
            </StateScreen>
        );
    }

    function handleMenuToggleClick() {
        setMenuVisible((menuVisible) => !menuVisible);
    }

    function handleMenuItemClick(label: string) {
        setActiveContent(() => label);
        setMenuVisible(() => false);
    }

    function renderContent() {
        switch (activeContent) {
            case 'regular':
                return <ContentRegularSessions />;
            case 'featured':
                return <ContentFeaturedSessions />;
            // case 'private':
            //   return <ContentPrivateSessions />;
            case 'lobby':
            default:
                return (
                    <LobbyGrid>
                        {activeSessions.map((session: Session) => {
                            const userSession =
                                user.current_session !== null &&
                                activeSessions.find(
                                    ({ id }) => id === user.current_session?.id,
                                );
                            if (
                                (user.current_session !== null &&
                                    user.current_session.id === session.id) ||
                                user.current_session === null ||
                                userSession === undefined ||
                                userSession === false
                            ) {
                                return (
                                    <LobbySession
                                        key={session.id}
                                        session={session}
                                        inAnotherSession={false}
                                    />
                                );
                            }
                            return (
                                <LobbySession
                                    key={session.id}
                                    session={session}
                                    inAnotherSession={true}
                                />
                            );
                        })}
                    </LobbyGrid>
                );
        }
    }

    return (
        <main className={styles.container}>
            <>
                <header className={styles.header}>
                    <h2 className={styles.heading}>
                        <button onClick={() => handleMenuItemClick('lobby')}>
                            Lobby
                        </button>
                    </h2>
                    {/*
          <button
            className={styles.toggle}
            onClick={() => handleMenuToggleClick()}
          >
            <b>☰</b> Session info
          </button>
          */}
                    {menuVisible && (
                        <nav className={styles.menu}>
                            <ul>
                                <li>
                                    <button
                                        onClick={() =>
                                            handleMenuItemClick('regular')
                                        }
                                    >
                                        Regular sessions
                                    </button>
                                </li>
                                <li>
                                    <button
                                        onClick={() =>
                                            handleMenuItemClick('featured')
                                        }
                                    >
                                        Featured sessions
                                    </button>
                                </li>
                                {/*
                <li>
                  <button onClick={() => handleMenuItemClick('private')}>
                    Private sessions
                  </button>
                </li>
                */}
                            </ul>
                        </nav>
                    )}
                </header>
                {renderContent()}
            </>
        </main>
    );
}

// Exports
export default Lobby;
